import * as THREE from 'three';

// ─── Constants ───────────────────────────────────────────────────────────────
const COURT_W    = 18;
const COURT_H    = 30;
const GOAL_W     = 4;
const GOAL_H     = 1.2;
const GOAL_INSET = 3.5;
const HALF_W     = COURT_W / 2;
const HALF_H     = COURT_H / 2;

const PLAYER_RADIUS  = 0.62;
const GOALIE_RADIUS  = 0.78;
const BALL_RADIUS    = 0.28;
const HANDLE_RADIUS  = 0.38;
const PICK_RADIUS    = 1.4;   // world-unit pick radius for player hit-test
const DRAG_THRESHOLD = 4;     // screen-px movement before it counts as a drag

const PASS_DURATION  = 0.5;
const SHOOT_DURATION = 0.8;
const SPEED_DOTS_PER_SEC = 2; // speed-indicator dots per second of motion
const PUCK_PICK      = 0.8;   // world-unit pick radius for grabbing the puck (step mode)
const ARROW_PICK     = 0.5;   // world-unit pick distance for tapping an arrow
const STEP_ARROW_W   = { slow: 0.1, normal: 0.15, sprint: 0.22 };

// ─── TacticEngine ─────────────────────────────────────────────────────────────
export default class TacticEngine {
    constructor(container, options = {}) {
        this.container       = container;
        this.onPlayerDrag    = options.onPlayerDrag    || (() => {});
        this.onPlayerSelect  = options.onPlayerSelect  || (() => {});
        this.onPassDraw      = options.onPassDraw      || (() => {});
        this.onShotDraw      = options.onShotDraw      || (() => {});
        this.onKeyframeDrag  = options.onKeyframeDrag  || (() => {});
        this.onDeleteKeyframe = options.onDeleteKeyframe || (() => {});
        this.onPuckDrop      = options.onPuckDrop      || (() => {});
        this.onArrowTap      = options.onArrowTap      || (() => {});

        // Three.js core
        this.scene    = null;
        this.camera   = null;
        this.renderer = null;

        // Scene objects
        this._playerMeshes   = [];   // THREE.Group per player
        this._pathObjects    = [];   // speed-dot meshes per player (array of arrays)
        this._handleMeshes   = [];   // keyframe handle rings for selected player [{mesh, playerIndex, kfT}]
        this._actionArrows   = [];   // persistent action line objects [{line, fromDot, toDot}]
        this._ballMesh       = null;
        this._selectionRing  = null;
        this._possessionRing = null;
        this._rubberBandLine = null; // live rubber-band line during pase/tiro

        // Step mode overlay: ghosts at step start, movement arrows, puck action
        this._stepView     = null;
        this._stepKey      = '';
        this._stepObjects  = [];
        this._stepHits     = [];     // [{kind: 'move'|'action', playerIndex, a, b}]
        this._previewArrow = null;
        this._puckDrag     = false;

        // State cache
        this._lastTactic          = null;
        this._editorMode          = 'setup';
        this._activeTool          = 'mover';
        this._selectedPlayerIndex = -1;

        // Drag state (mover tool)
        this._dragIndex       = -1;
        this._dragHandle      = null;   // {playerIndex, kfT} if dragging a handle
        this._dragStartScreen = null;   // {x, y}
        this._hasDragged      = false;

        // Rubber-band state (pase / tiro tools)
        this._rbFromIndex = -1;
        this._rbFromPos   = null;

        this._init();
    }

    // ─── Init ─────────────────────────────────────────────────────────────────
    _init() {
        const w = this.container.clientWidth  || 1;
        const h = this.container.clientHeight || 1;

        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0x0a1628);

        this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 100);
        this.camera.position.set(0, 0, 20);
        this.camera.lookAt(0, 0, 0);
        this._applyCamera(w, h);

        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        this.renderer.setSize(w, h);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        // CSS fill so the canvas is always visually 100%×100% of its container,
        // even before the ResizeObserver fires on mobile.
        this.renderer.domElement.style.width       = '100%';
        this.renderer.domElement.style.height      = '100%';
        this.renderer.domElement.style.touchAction = 'none';
        this.container.appendChild(this.renderer.domElement);

        this.scene.add(new THREE.AmbientLight(0xffffff, 1.0));
        this._buildCourt();
        this._buildBallMesh();
        this._buildSelectionRing();
        this._buildPossessionRing();

        this._resizeHandler      = () => this._onResize();
        this._pointerDownHandler = (e) => this._onPointerDown(e);
        this._pointerMoveHandler = (e) => this._onPointerMove(e);
        this._pointerUpHandler   = (e) => this._onPointerUp(e);

        window.addEventListener('resize', this._resizeHandler);
        this._resizeObserver = new ResizeObserver(() => this._onResize());
        this._resizeObserver.observe(this.container);
        this.renderer.domElement.addEventListener('pointerdown', this._pointerDownHandler);
    }

    // ─── Court ────────────────────────────────────────────────────────────────
    _buildCourt() {
        this.scene.add(new THREE.Mesh(
            new THREE.PlaneGeometry(COURT_W, COURT_H),
            new THREE.MeshBasicMaterial({ color: 0x1a4a7a })
        ));
        const lw = 0.18;
        this._addRect(0,       HALF_H,  COURT_W, lw,      0xffffff);
        this._addRect(0,      -HALF_H,  COURT_W, lw,      0xffffff);
        this._addRect(-HALF_W, 0,       lw,      COURT_H, 0xffffff);
        this._addRect( HALF_W, 0,       lw,      COURT_H, 0xffffff);
        this._addRect(0, 0, COURT_W, lw * 0.8, 0xdd2233);

        this.scene.add(this._makeRing(2.5, 2.7, 0xffffff, 0, 0, 0.01));
        const dot = new THREE.Mesh(new THREE.CircleGeometry(0.2, 16), new THREE.MeshBasicMaterial({ color: 0xffffff }));
        dot.position.z = 0.01;
        this.scene.add(dot);

        [[-5, 8], [5, 8], [-5, -8], [5, -8]].forEach(([x, y]) => {
            const d = new THREE.Mesh(new THREE.CircleGeometry(0.2, 16), new THREE.MeshBasicMaterial({ color: 0xdd2233 }));
            d.position.set(x, y, 0.01);
            this.scene.add(d);
        });

        const cR = 2;
        [
            [-HALF_W + cR,  HALF_H - cR, Math.PI / 2,      Math.PI],
            [ HALF_W - cR,  HALF_H - cR, 0,                Math.PI / 2],
            [-HALF_W + cR, -HALF_H + cR, Math.PI,          3 * Math.PI / 2],
            [ HALF_W - cR, -HALF_H + cR, 3 * Math.PI / 2,  2 * Math.PI],
        ].forEach(([x, y, start, end]) => {
            const pts = [];
            for (let a = start; a <= end + 0.05; a += 0.08) {
                pts.push(new THREE.Vector3(x + Math.cos(a) * cR, y + Math.sin(a) * cR, 0.01));
            }
            this.scene.add(new THREE.Line(
                new THREE.BufferGeometry().setFromPoints(pts),
                new THREE.LineBasicMaterial({ color: 0xffffff })
            ));
        });

        this._buildGoal(0,  HALF_H - GOAL_INSET + GOAL_H / 2, 'top');
        this._buildGoal(0, -HALF_H + GOAL_INSET - GOAL_H / 2, 'bottom');
        this._buildCrease(0,  HALF_H - GOAL_INSET, Math.PI, Math.PI);
        this._buildCrease(0, -HALF_H + GOAL_INSET, 0,       Math.PI);
    }

    _buildGoal(x, y, side) {
        const net = new THREE.Mesh(new THREE.PlaneGeometry(GOAL_W, GOAL_H),
            new THREE.MeshBasicMaterial({ color: 0x888888, transparent: true, opacity: 0.6 }));
        net.position.set(x, y, 0.01);
        this.scene.add(net);
        [-GOAL_W / 2, GOAL_W / 2].forEach(px => {
            const post = new THREE.Mesh(new THREE.PlaneGeometry(0.22, GOAL_H + 0.1),
                new THREE.MeshBasicMaterial({ color: 0xffffff }));
            post.position.set(x + px, y, 0.03);
            this.scene.add(post);
        });
        const barY = side === 'top' ? y + GOAL_H / 2 : y - GOAL_H / 2;
        const bar  = new THREE.Mesh(new THREE.PlaneGeometry(GOAL_W + 0.22, 0.22),
            new THREE.MeshBasicMaterial({ color: 0xffffff }));
        bar.position.set(x, barY, 0.03);
        this.scene.add(bar);
    }

    _buildCrease(x, y, startAngle, endAngle) {
        const shape = new THREE.Shape();
        const r = 3;
        shape.moveTo(x - GOAL_W / 2, y);
        for (let a = startAngle; a <= endAngle + Math.PI + 0.05; a += 0.05) {
            shape.lineTo(x + Math.cos(a) * r, y + Math.sin(a) * r);
        }
        shape.lineTo(x + GOAL_W / 2, y);
        const mesh = new THREE.Mesh(new THREE.ShapeGeometry(shape),
            new THREE.MeshBasicMaterial({ color: 0x4488cc, transparent: true, opacity: 0.25, side: THREE.DoubleSide }));
        mesh.position.z = 0.01;
        this.scene.add(mesh);
    }

    _addRect(x, y, w, h, color) {
        const mesh = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ color }));
        mesh.position.set(x, y, 0.01);
        this.scene.add(mesh);
        return mesh;
    }

    _makeRing(inner, outer, color, x, y, z) {
        const mesh = new THREE.Mesh(new THREE.RingGeometry(inner, outer, 48),
            new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide }));
        mesh.position.set(x, y, z);
        return mesh;
    }

    // ─── Player meshes ────────────────────────────────────────────────────────
    _makeLabelTexture(label) {
        const size = 64;
        const canvas = document.createElement('canvas');
        canvas.width = size; canvas.height = size;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, size, size);
        ctx.fillStyle = 'rgba(255,255,255,0.92)';
        const fs = label.length > 3 ? 15 : label.length > 2 ? 19 : 23;
        ctx.font = `bold ${fs}px Arial, sans-serif`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(label, size / 2, size / 2);
        return new THREE.CanvasTexture(canvas);
    }

    _createPlayerMesh(player) {
        const isGoalie = player.role === 'goalie';
        const r        = isGoalie ? GOALIE_RADIUS : PLAYER_RADIUS;
        const colorHex = parseInt(player.color.replace('#', ''), 16);
        const group    = new THREE.Group();

        group.add(new THREE.Mesh(new THREE.CircleGeometry(r, 24),
            new THREE.MeshBasicMaterial({ color: colorHex })));
        // Rival players get a dark outline so they read as the other team at a glance
        const outlineColor = player.team === 'opponent' ? 0x111111 : 0xffffff;
        const outline = new THREE.Mesh(new THREE.RingGeometry(r, r + 0.13, 24),
            new THREE.MeshBasicMaterial({ color: outlineColor, side: THREE.DoubleSide }));
        outline.position.z = 0.001;
        group.add(outline);
        const inner = new THREE.Mesh(new THREE.CircleGeometry(r * 0.55, 16),
            new THREE.MeshBasicMaterial({ color: new THREE.Color(colorHex).multiplyScalar(0.6).getHex() }));
        inner.position.z = 0.002;
        group.add(inner);
        const label = new THREE.Mesh(new THREE.PlaneGeometry(r * 1.2, r * 1.2),
            new THREE.MeshBasicMaterial({ map: this._makeLabelTexture(player.label), transparent: true }));
        label.position.z = 0.003;
        group.add(label);
        group.position.z = 0.05;
        return group;
    }

    _rebuildPlayers(tactic) {
        this._playerMeshes.forEach(m => this.scene.remove(m));
        this._pathObjects.forEach(dots => dots.forEach(d => this.scene.remove(d)));
        this._playerMeshes = [];
        this._pathObjects  = [];

        if (!tactic || !tactic.players) return;

        tactic.players.forEach(player => {
            const mesh = this._createPlayerMesh(player);
            this.scene.add(mesh);
            this._playerMeshes.push(mesh);
            this._pathObjects.push([]);
        });

        this._rebuildPaths(tactic);
    }

    // Speed-dot path: dots spaced evenly in time — dense = slow, sparse = fast
    _rebuildPaths(tactic) {
        this._pathObjects.forEach(dots => dots.forEach(d => this.scene.remove(d)));
        this._pathObjects = tactic.players.map(() => []);

        tactic.players.forEach((player, pi) => {
            const kfs = [...player.keyframes].sort((a, b) => a.t - b.t);
            if (kfs.length < 2) return;

            const colorHex = parseInt(player.color.replace('#', ''), 16);

            for (let i = 0; i < kfs.length - 1; i++) {
                const k0 = kfs[i];
                const k1 = kfs[i + 1];
                const dt  = k1.t - k0.t;
                const n   = Math.max(2, Math.round(dt * SPEED_DOTS_PER_SEC));

                for (let j = 0; j <= n; j++) {
                    const alpha = j / n;
                    const x = k0.x + (k1.x - k0.x) * alpha;
                    const y = k0.y + (k1.y - k0.y) * alpha;
                    const dot = new THREE.Mesh(
                        new THREE.CircleGeometry(0.12, 8),
                        new THREE.MeshBasicMaterial({ color: colorHex, transparent: true, opacity: 0.55 })
                    );
                    dot.position.set(x, y, 0.02);
                    this.scene.add(dot);
                    this._pathObjects[pi].push(dot);
                }
            }
        });
    }

    // ─── Keyframe handles ─────────────────────────────────────────────────────
    _rebuildHandles(tactic, selectedIndex) {
        this._handleMeshes.forEach(({ mesh }) => this.scene.remove(mesh));
        this._handleMeshes = [];

        if (selectedIndex < 0 || !tactic || !tactic.players[selectedIndex]) return;

        const player   = tactic.players[selectedIndex];
        const colorHex = parseInt(player.color.replace('#', ''), 16);

        player.keyframes.forEach(kf => {
            const ring = new THREE.Mesh(
                new THREE.RingGeometry(HANDLE_RADIUS, HANDLE_RADIUS + 0.15, 20),
                new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.DoubleSide })
            );
            ring.position.set(kf.x, kf.y, 0.12);
            this.scene.add(ring);

            const fill = new THREE.Mesh(
                new THREE.CircleGeometry(HANDLE_RADIUS, 20),
                new THREE.MeshBasicMaterial({ color: colorHex, transparent: true, opacity: 0.7 })
            );
            fill.position.set(kf.x, kf.y, 0.11);
            this.scene.add(fill);

            this._handleMeshes.push({ mesh: ring,  playerIndex: selectedIndex, kfT: kf.t });
            this._handleMeshes.push({ mesh: fill,  playerIndex: selectedIndex, kfT: kf.t });
        });
    }

    // ─── Selection ring ───────────────────────────────────────────────────────
    _buildSelectionRing() {
        const r    = GOALIE_RADIUS + 0.18;
        const ring = new THREE.Mesh(
            new THREE.RingGeometry(r, r + 0.30, 32),
            new THREE.MeshBasicMaterial({ color: 0xffdd00, transparent: true, opacity: 0.85, side: THREE.DoubleSide })
        );
        ring.position.z = 0.09;
        ring.visible = false;
        this.scene.add(ring);
        this._selectionRing = ring;
    }

    // ─── Possession ring ──────────────────────────────────────────────────────
    _buildPossessionRing() {
        const r    = GOALIE_RADIUS + 0.55;
        const ring = new THREE.Mesh(
            new THREE.RingGeometry(r, r + 0.18, 32),
            new THREE.MeshBasicMaterial({ color: 0xff8800, transparent: true, opacity: 0.55, side: THREE.DoubleSide })
        );
        ring.position.z = 0.08;
        ring.visible = false;
        this.scene.add(ring);
        this._possessionRing = ring;
    }

    // ─── Ball ─────────────────────────────────────────────────────────────────
    _buildBallMesh() {
        const group = new THREE.Group();
        group.add(new THREE.Mesh(new THREE.CircleGeometry(BALL_RADIUS, 16),
            new THREE.MeshBasicMaterial({ color: 0xff8800 })));
        const outline = new THREE.Mesh(new THREE.RingGeometry(BALL_RADIUS, BALL_RADIUS + 0.08, 16),
            new THREE.MeshBasicMaterial({ color: 0x000000, side: THREE.DoubleSide }));
        outline.position.z = 0.001;
        group.add(outline);
        group.position.z = 0.1;
        this._ballMesh = group;
        this.scene.add(group);
    }

    // ─── Action arrows ────────────────────────────────────────────────────────
    _rebuildActionArrows(tactic) {
        this._actionArrows.forEach(({ line, tip }) => {
            this.scene.remove(line);
            if (tip) this.scene.remove(tip);
        });
        this._actionArrows = [];

        if (!tactic || !tactic.actions || !tactic.players) return;

        tactic.actions.forEach(action => {
            const fromPlayer = tactic.players[action.fromPlayerIndex];
            if (!fromPlayer) return;

            const fromPos = this._interpolate(fromPlayer.keyframes, action.t);
            let   toPos;

            if (action.type === 'pass' && action.toPlayerIndex != null) {
                const toPlayer = tactic.players[action.toPlayerIndex];
                if (!toPlayer) return;
                toPos = this._interpolate(toPlayer.keyframes, action.t + PASS_DURATION);
            } else if (action.type === 'shoot') {
                toPos = { x: 0, y: HALF_H - GOAL_INSET };
            } else {
                return;
            }

            const color = action.type === 'pass' ? 0x44ff88 : 0xff4444;

            const pts = [
                new THREE.Vector3(fromPos.x, fromPos.y, 0.06),
                new THREE.Vector3(toPos.x,   toPos.y,   0.06),
            ];
            const line = new THREE.Line(
                new THREE.BufferGeometry().setFromPoints(pts),
                new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.45 })
            );
            this.scene.add(line);

            // Arrowhead dot at destination
            const tip = new THREE.Mesh(
                new THREE.CircleGeometry(0.22, 10),
                new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.45 })
            );
            tip.position.set(toPos.x, toPos.y, 0.07);
            this.scene.add(tip);

            this._actionArrows.push({ line, tip, action });
        });
    }

    _updateActionArrowOpacity(currentTime) {
        this._actionArrows.forEach(({ line, tip, action }) => {
            const close  = Math.abs(currentTime - action.t) < 0.4;
            const opacity = close ? 1.0 : 0.3;
            line.material.opacity = opacity;
            if (tip) tip.material.opacity = opacity;
        });
    }

    // ─── Step overlay ─────────────────────────────────────────────────────────
    // Flat arrow (body quad + triangle head), optionally dashed
    _makeArrow(from, to, color, width, opacity, { dashed = false, startGap = 0, endGap = 0, z = 0.04 } = {}) {
        const group = new THREE.Group();
        const dx = to.x - from.x, dy = to.y - from.y;
        const len = Math.hypot(dx, dy);
        if (len < 0.05 || len <= startGap + endGap) return group;
        const ux = dx / len, uy = dy / len;
        const head = Math.min(width * 3.2, (len - startGap - endGap) * 0.5);
        const bodyStart = startGap, bodyEnd = len - endGap - head * 0.8;
        const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity, depthWrite: false, side: THREE.DoubleSide });
        const addSeg = (s0, s1) => {
            if (s1 - s0 < 0.02) return;
            const seg = new THREE.Mesh(new THREE.PlaneGeometry(s1 - s0, width), mat);
            const mid = (s0 + s1) / 2;
            seg.position.set(from.x + ux * mid, from.y + uy * mid, z);
            seg.rotation.z = Math.atan2(uy, ux);
            group.add(seg);
        };
        if (dashed) {
            for (let d = bodyStart; d < bodyEnd; d += 0.55) addSeg(d, Math.min(d + 0.32, bodyEnd));
        } else {
            addSeg(bodyStart, bodyEnd);
        }
        const tipD = len - endGap;
        const tx = from.x + ux * tipD, ty = from.y + uy * tipD;
        const bx = tx - ux * head, by = ty - uy * head;
        const px = -uy * head * 0.6, py = ux * head * 0.6;
        const shape = new THREE.Shape();
        shape.moveTo(tx, ty);
        shape.lineTo(bx + px, by + py);
        shape.lineTo(bx - px, by - py);
        shape.closePath();
        const tip = new THREE.Mesh(new THREE.ShapeGeometry(shape), mat);
        tip.position.z = z;
        group.add(tip);
        return group;
    }

    _disposeGroup(obj) {
        this.scene.remove(obj);
        obj.traverse(o => {
            if (o.geometry) o.geometry.dispose();
            if (o.material) o.material.dispose();
        });
    }

    _rebuildStepOverlay() {
        this._stepObjects.forEach(o => this._disposeGroup(o));
        this._stepObjects = [];
        this._stepHits = [];
        const v = this._stepView;
        const tactic = this._lastTactic;
        if (!v || !tactic) return;
        const add = o => { this.scene.add(o); this._stepObjects.push(o); };
        const colorOf = i => parseInt((tactic.players[i]?.color || '#ffffff').replace('#', ''), 16);
        const radiusOf = i => (tactic.players[i]?.role === 'goalie' ? GOALIE_RADIUS : PLAYER_RADIUS);

        // Earlier steps, very faint, for context
        (v.prevArrows || []).forEach(a => {
            add(this._makeArrow(a.from, a.to, colorOf(a.playerIndex), 0.07, 0.18, { endGap: 0.3, z: 0.02 }));
        });

        // This step's moves: ghost at the start + arrow to the destination
        (v.moves || []).forEach(m => {
            const color = colorOf(m.playerIndex);
            const r = radiusOf(m.playerIndex);
            const ghost = new THREE.Group();
            ghost.add(new THREE.Mesh(new THREE.CircleGeometry(r, 24),
                new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.25, depthWrite: false })));
            ghost.add(new THREE.Mesh(new THREE.RingGeometry(r, r + 0.08, 24),
                new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.35, side: THREE.DoubleSide, depthWrite: false })));
            ghost.position.set(m.from.x, m.from.y, 0.03);
            add(ghost);
            add(this._makeArrow(m.from, m.to, color, STEP_ARROW_W[m.speed] || STEP_ARROW_W.normal, 0.9,
                { dashed: m.speed === 'slow', startGap: r * 0.6, endGap: r + 0.1 }));
            this._stepHits.push({ kind: 'move', playerIndex: m.playerIndex, a: m.from, b: m.to });
        });

        // Puck action
        const act = v.action;
        if (act && act.from && act.to) {
            const isPass = act.type === 'pass';
            add(this._makeArrow(act.from, act.to, isPass ? 0x44ff88 : 0xff4444, 0.12, 0.95,
                { dashed: true, startGap: 0.5, endGap: isPass ? PLAYER_RADIUS + 0.15 : 0.1, z: 0.13 }));
            this._stepHits.push({ kind: 'action', a: act.from, b: act.to });
        }
    }

    // Puck position while editing a step: next to its carrier (or in the net after a shot)
    _stepPuckPos() {
        const v = this._stepView;
        if (!v) return null;
        if (v.carrierIndex < 0) return { x: 0, y: HALF_H - GOAL_INSET + 0.4 };
        const mesh = this._playerMeshes[v.carrierIndex];
        if (!mesh) return null;
        const p = v.carrierPos || mesh.position;
        return { x: p.x + 0.62, y: p.y + 0.62 };
    }

    _segmentDistance(p, a, b) {
        const dx = b.x - a.x, dy = b.y - a.y;
        const l2 = dx * dx + dy * dy || 1e-6;
        const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2));
        return Math.hypot(p.x - (a.x + dx * t), p.y - (a.y + dy * t));
    }

    _updatePreviewArrow(playerIndex, to) {
        if (this._previewArrow) { this._disposeGroup(this._previewArrow); this._previewArrow = null; }
        const v = this._stepView;
        const from = v && v.starts && v.starts[playerIndex];
        if (!from || !to) return;
        const color = parseInt((this._lastTactic.players[playerIndex]?.color || '#ffffff').replace('#', ''), 16);
        this._previewArrow = this._makeArrow(from, to, color, STEP_ARROW_W.normal, 0.6, { startGap: 0.3, endGap: PLAYER_RADIUS + 0.1 });
        this.scene.add(this._previewArrow);
    }

    // ─── Rubber-band line (pass/tiro drawing) ─────────────────────────────────
    _startRubberBand(fromPos) {
        this._clearRubberBand();
        const pts = [
            new THREE.Vector3(fromPos.x, fromPos.y, 0.15),
            new THREE.Vector3(fromPos.x, fromPos.y, 0.15),
        ];
        const line = new THREE.Line(
            new THREE.BufferGeometry().setFromPoints(pts),
            new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.8 })
        );
        this.scene.add(line);
        this._rubberBandLine = line;
    }

    _updateRubberBand(fromPos, toPos) {
        if (!this._rubberBandLine) return;
        const pts = [
            new THREE.Vector3(fromPos.x, fromPos.y, 0.15),
            new THREE.Vector3(toPos.x,   toPos.y,   0.15),
        ];
        this._rubberBandLine.geometry.setFromPoints(pts);
    }

    _clearRubberBand() {
        if (this._rubberBandLine) {
            this.scene.remove(this._rubberBandLine);
            this._rubberBandLine = null;
        }
    }

    // ─── Keyframe interpolation ───────────────────────────────────────────────
    _interpolate(keyframes, t) {
        if (!keyframes || keyframes.length === 0) return { x: 0, y: 0 };
        const sorted = [...keyframes].sort((a, b) => a.t - b.t);
        if (t <= sorted[0].t) return { x: sorted[0].x, y: sorted[0].y };
        if (t >= sorted[sorted.length - 1].t) {
            const last = sorted[sorted.length - 1];
            return { x: last.x, y: last.y };
        }
        for (let i = 0; i < sorted.length - 1; i++) {
            const a = sorted[i]; const b = sorted[i + 1];
            if (t >= a.t && t <= b.t) {
                const span  = b.t - a.t;
                const alpha = span < 0.0001 ? 0 : (t - a.t) / span;
                return { x: a.x + (b.x - a.x) * alpha, y: a.y + (b.y - a.y) * alpha };
            }
        }
        const last = sorted[sorted.length - 1];
        return { x: last.x, y: last.y };
    }

    // ─── Ball + possession ────────────────────────────────────────────────────
    _computeBallAndPossession(tactic, currentTime) {
        if (!tactic || !tactic.players || tactic.players.length === 0) {
            return { pos: { x: 0, y: -HALF_H + GOAL_INSET }, possessorIndex: 0 };
        }

        const actions     = (tactic.actions || []).slice().sort((a, b) => a.t - b.t);
        const players     = tactic.players;
        const goalPos     = { x: 0, y: HALF_H - GOAL_INSET };
        const startIndex  = players[tactic.puckStart] ? tactic.puckStart : 0;
        const holder      = players[startIndex];
        let   ballPos     = holder && holder.keyframes.length > 0
            ? this._interpolate(holder.keyframes, currentTime)
            : { x: 0, y: -HALF_H + GOAL_INSET };
        let   possessorIndex = startIndex;

        for (const action of actions) {
            if (action.t > currentTime) break;
            const from = players[action.fromPlayerIndex];
            if (!from) continue;

            if (action.type === 'pass' && action.toPlayerIndex != null) {
                const to = players[action.toPlayerIndex];
                if (!to) continue;
                const elapsed = currentTime - action.t;
                if (elapsed < PASS_DURATION) {
                    const alpha   = elapsed / PASS_DURATION;
                    const fromPos = this._interpolate(from.keyframes, action.t);
                    const toPos   = this._interpolate(to.keyframes, action.t + PASS_DURATION);
                    ballPos = { x: fromPos.x + (toPos.x - fromPos.x) * alpha, y: fromPos.y + (toPos.y - fromPos.y) * alpha };
                    possessorIndex = -1; // in transit
                } else {
                    ballPos        = this._interpolate(to.keyframes, currentTime);
                    possessorIndex = action.toPlayerIndex;
                }
            } else if (action.type === 'shoot') {
                const elapsed = currentTime - action.t;
                if (elapsed < SHOOT_DURATION) {
                    const alpha   = elapsed / SHOOT_DURATION;
                    const fromPos = this._interpolate(from.keyframes, action.t);
                    ballPos = { x: fromPos.x + (goalPos.x - fromPos.x) * alpha, y: fromPos.y + (goalPos.y - fromPos.y) * alpha };
                    possessorIndex = -1;
                } else {
                    ballPos        = { ...goalPos };
                    possessorIndex = -1;
                }
            }
        }

        return { pos: ballPos, possessorIndex };
    }

    // ─── Public update ────────────────────────────────────────────────────────
    update(tactic, currentTime, options = {}) {
        const {
            editorMode          = 'setup',
            activeTool          = 'mover',
            selectedPlayerIndex = -1,
            stepView            = null,
        } = options;

        const tacticChanged    = tactic !== this._lastTactic;
        const selectionChanged = selectedPlayerIndex !== this._selectedPlayerIndex;

        this._editorMode          = editorMode;
        this._activeTool          = activeTool;
        this._selectedPlayerIndex = selectedPlayerIndex;

        // Rebuild static elements when tactic reference changes
        if (tacticChanged) {
            this._rebuildPlayers(tactic);
            this._rebuildActionArrows(tactic);
            this._lastTactic = tactic;
        }

        // Step overlay (only rebuilt when its content changes)
        this._stepView = editorMode === 'steps' || editorMode === 'setup' ? stepView : null;
        const stepKey = this._stepView ? JSON.stringify(this._stepView) : '';
        if (tacticChanged || stepKey !== this._stepKey) {
            this._stepKey = stepKey;
            this._rebuildStepOverlay();
        }

        // Rebuild handles when selection changes
        if (tacticChanged || selectionChanged) {
            this._rebuildHandles(tactic, selectedPlayerIndex);
            // Rebuild paths too (they depend on keyframes which may have changed)
            if (tactic) this._rebuildPaths(tactic);
        }

        if (!tactic) {
            this.renderer.render(this.scene, this.camera);
            return;
        }

        // Position players
        tactic.players.forEach((player, i) => {
            const mesh = this._playerMeshes[i];
            if (!mesh) return;
            const pos = this._interpolate(player.keyframes, currentTime);
            mesh.position.x = pos.x;
            mesh.position.y = pos.y;
        });

        // Selection ring
        if (selectedPlayerIndex >= 0 && tactic.players[selectedPlayerIndex]) {
            const pos = this._interpolate(tactic.players[selectedPlayerIndex].keyframes, currentTime);
            this._selectionRing.position.set(pos.x, pos.y, 0.09);
            this._selectionRing.visible = true;
        } else {
            this._selectionRing.visible = false;
        }

        // Timeline-based paths/arrows are replaced by the step overlay in step mode
        const stepMode = editorMode === 'steps';
        this._pathObjects.forEach(dots => dots.forEach(d => { d.visible = !stepMode; }));
        this._actionArrows.forEach(({ line, tip }) => { line.visible = !stepMode; if (tip) tip.visible = !stepMode; });
        this._handleMeshes.forEach(({ mesh }) => { mesh.visible = !stepMode; });

        // Ball + possession ring
        let { pos: ballPos, possessorIndex } = this._computeBallAndPossession(tactic, currentTime);
        if (this._stepView) {
            // While editing a step the puck sits with whoever has it at the step start
            possessorIndex = this._stepView.carrierIndex;
            ballPos = this._stepPuckPos() || { x: 0, y: HALF_H - GOAL_INSET };
        }
        this._ballMesh.position.x = ballPos.x;
        this._ballMesh.position.y = ballPos.y;

        if (possessorIndex >= 0 && tactic.players[possessorIndex]) {
            const pp = this._interpolate(tactic.players[possessorIndex].keyframes, currentTime);
            this._possessionRing.position.set(pp.x, pp.y, 0.08);
            this._possessionRing.visible = true;
        } else {
            this._possessionRing.visible = false;
        }

        // Action arrow opacity (flash near event time)
        this._updateActionArrowOpacity(currentTime);

        this.renderer.render(this.scene, this.camera);
    }

    // ─── Pointer helpers ──────────────────────────────────────────────────────
    _screenToWorld(screenX, screenY) {
        const rect = this.renderer.domElement.getBoundingClientRect();
        const ndcX =  ((screenX - rect.left) / rect.width)  * 2 - 1;
        const ndcY = -((screenY - rect.top)  / rect.height) * 2 + 1;
        const vec  = new THREE.Vector3(ndcX, ndcY, 0);
        vec.unproject(this.camera);
        return { x: vec.x, y: vec.y };
    }

    _clampToCourt(x, y, r = PLAYER_RADIUS) {
        return {
            x: Math.max(-HALF_W + r, Math.min(HALF_W - r, x)),
            y: Math.max(-HALF_H + r, Math.min(HALF_H - r, y)),
        };
    }

    _nearestPlayer(worldX, worldY) {
        if (!this._lastTactic) return -1;
        let nearest = -1;
        let minD    = PICK_RADIUS;
        this._lastTactic.players.forEach((p, i) => {
            const mesh = this._playerMeshes[i];
            if (!mesh) return;
            const dx = worldX - mesh.position.x;
            const dy = worldY - mesh.position.y;
            const d  = Math.sqrt(dx * dx + dy * dy);
            if (d < minD) { minD = d; nearest = i; }
        });
        return nearest;
    }

    _nearestHandle(worldX, worldY) {
        const HPICK = HANDLE_RADIUS + 0.5;
        for (const h of this._handleMeshes) {
            if (!h.mesh.visible) continue;
            const dx = worldX - h.mesh.position.x;
            const dy = worldY - h.mesh.position.y;
            if (Math.sqrt(dx * dx + dy * dy) < HPICK && h.kfT !== undefined) return h;
        }
        return null;
    }

    // ─── Pointer down ─────────────────────────────────────────────────────────
    _onPointerDown(e) {
        const world = this._screenToWorld(e.clientX, e.clientY);

        // ─── Step mode: puck → player → arrow ────────────────────────────────
        if (this._stepView) {
            const v = this._stepView;
            const puck = this._ballMesh.position;
            if (v.carrierIndex >= 0 && Math.hypot(world.x - puck.x, world.y - puck.y) < PUCK_PICK) {
                this._puckDrag = true;
                this._rbFromPos = { x: puck.x, y: puck.y };
                this._startRubberBand(this._rbFromPos);
                e.target.setPointerCapture(e.pointerId);
                window.addEventListener('pointermove', this._pointerMoveHandler);
                window.addEventListener('pointerup',   this._pointerUpHandler);
                return;
            }
            const i = this._nearestPlayer(world.x, world.y);
            if (i >= 0) {
                this._dragIndex       = i;
                this._dragStartScreen = { x: e.clientX, y: e.clientY };
                this._hasDragged      = false;
                e.target.setPointerCapture(e.pointerId);
                window.addEventListener('pointermove', this._pointerMoveHandler);
                window.addEventListener('pointerup',   this._pointerUpHandler);
                return;
            }
            let best = null, bestD = ARROW_PICK;
            this._stepHits.forEach(h => {
                const d = this._segmentDistance(world, h.a, h.b);
                if (d < bestD) { bestD = d; best = h; }
            });
            if (best) this.onArrowTap({ kind: best.kind, playerIndex: best.playerIndex }, e.clientX, e.clientY);
            else this.onArrowTap(null);
            return;
        }

        // ─── Pase / Tiro: rubber-band from nearest player ────────────────────
        if (this._activeTool === 'pase' || this._activeTool === 'tiro') {
            const i = this._nearestPlayer(world.x, world.y);
            if (i < 0) return;
            const pos = this._interpolate(this._lastTactic.players[i].keyframes, 0); // approximate — mesh position
            const meshPos = { x: this._playerMeshes[i].position.x, y: this._playerMeshes[i].position.y };
            this._rbFromIndex = i;
            this._rbFromPos   = meshPos;
            this._startRubberBand(meshPos);
            e.target.setPointerCapture(e.pointerId);
            window.addEventListener('pointermove', this._pointerMoveHandler);
            window.addEventListener('pointerup',   this._pointerUpHandler);
            return;
        }

        // ─── Mover tool: check handles first, then players ────────────────────
        const handle = this._nearestHandle(world.x, world.y);
        if (handle) {
            this._dragHandle      = handle;
            this._dragStartScreen = { x: e.clientX, y: e.clientY };
            this._hasDragged      = false;
            e.target.setPointerCapture(e.pointerId);
            window.addEventListener('pointermove', this._pointerMoveHandler);
            window.addEventListener('pointerup',   this._pointerUpHandler);
            return;
        }

        const i = this._nearestPlayer(world.x, world.y);
        if (i >= 0) {
            this._dragIndex       = i;
            this._dragStartScreen = { x: e.clientX, y: e.clientY };
            this._hasDragged      = false;
            // Select immediately on pointer-down (feels responsive)
            this.onPlayerSelect(i);
            e.target.setPointerCapture(e.pointerId);
            window.addEventListener('pointermove', this._pointerMoveHandler);
            window.addEventListener('pointerup',   this._pointerUpHandler);
        }
    }

    // ─── Pointer move ─────────────────────────────────────────────────────────
    _onPointerMove(e) {
        const world   = this._screenToWorld(e.clientX, e.clientY);
        const clamped = this._clampToCourt(world.x, world.y);

        // Puck drag (step mode)
        if (this._puckDrag) {
            this._updateRubberBand(this._rbFromPos, world);
            this._ballMesh.position.set(world.x, world.y, this._ballMesh.position.z);
            this.renderer.render(this.scene, this.camera);
            return;
        }

        // Rubber-band
        if (this._rbFromIndex >= 0 && this._rbFromPos) {
            this._updateRubberBand(this._rbFromPos, clamped);
            this.renderer.render(this.scene, this.camera);
            return;
        }

        // Handle drag
        if (this._dragHandle) {
            const dx = e.clientX - this._dragStartScreen.x;
            const dy = e.clientY - this._dragStartScreen.y;
            if (Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD) this._hasDragged = true;
            if (this._hasDragged) {
                // Move the handle mesh live for preview
                const h = this._handleMeshes.filter(hm => hm.kfT === this._dragHandle.kfT && hm.playerIndex === this._dragHandle.playerIndex);
                h.forEach(hm => hm.mesh.position.set(clamped.x, clamped.y, hm.mesh.position.z));
                this.renderer.render(this.scene, this.camera);
            }
            return;
        }

        // Player drag
        if (this._dragIndex >= 0) {
            const dx = e.clientX - this._dragStartScreen.x;
            const dy = e.clientY - this._dragStartScreen.y;
            if (Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD) this._hasDragged = true;
            if (this._hasDragged) {
                const mesh = this._playerMeshes[this._dragIndex];
                if (mesh) { mesh.position.x = clamped.x; mesh.position.y = clamped.y; }
                if (this._stepView) this._updatePreviewArrow(this._dragIndex, clamped);
                this.renderer.render(this.scene, this.camera);
            }
        }
    }

    // ─── Pointer up ───────────────────────────────────────────────────────────
    _onPointerUp(e) {
        const world   = this._screenToWorld(e.clientX, e.clientY);
        const clamped = this._clampToCourt(world.x, world.y);

        window.removeEventListener('pointermove', this._pointerMoveHandler);
        window.removeEventListener('pointerup',   this._pointerUpHandler);

        if (this._previewArrow) { this._disposeGroup(this._previewArrow); this._previewArrow = null; }

        // ─── Finish puck drag (step mode) ─────────────────────────────────────
        if (this._puckDrag) {
            this._puckDrag = false;
            this._rbFromPos = null;
            this._clearRubberBand();
            const carrier = this._stepView ? this._stepView.carrierIndex : -1;
            const to = this._nearestPlayer(world.x, world.y);
            const inShotZone = Math.abs(world.x) < GOAL_W / 2 + 1.5 && world.y > HALF_H - GOAL_INSET - 3;
            if (to >= 0 && to !== carrier) this.onPuckDrop({ type: 'pass', to });
            else if (inShotZone) this.onPuckDrop({ type: 'shoot' });
            // Snap the puck back if nothing changed
            const p = this._stepPuckPos();
            if (p) this._ballMesh.position.set(p.x, p.y, this._ballMesh.position.z);
            this.renderer.render(this.scene, this.camera);
            return;
        }

        // ─── Finish rubber-band ───────────────────────────────────────────────
        if (this._rbFromIndex >= 0) {
            const fromIndex = this._rbFromIndex;
            this._rbFromIndex = -1;
            this._rbFromPos   = null;
            this._clearRubberBand();

            if (this._activeTool === 'pase') {
                const toIndex = this._nearestPlayer(world.x, world.y);
                if (toIndex >= 0 && toIndex !== fromIndex) {
                    this.onPassDraw(fromIndex, toIndex);
                }
            } else if (this._activeTool === 'tiro') {
                // Accept shot if released anywhere on field
                this.onShotDraw(fromIndex);
            }
            return;
        }

        // ─── Finish handle drag ───────────────────────────────────────────────
        if (this._dragHandle) {
            const { playerIndex, kfT } = this._dragHandle;
            this._dragHandle = null;
            if (this._hasDragged) {
                this.onKeyframeDrag(playerIndex, kfT, clamped.x, clamped.y);
            }
            this._hasDragged = false;
            return;
        }

        // ─── Finish player drag ───────────────────────────────────────────────
        if (this._dragIndex >= 0) {
            const idx = this._dragIndex;
            this._dragIndex = -1;
            if (this._hasDragged) {
                this.onPlayerDrag(idx, clamped.x, clamped.y);
            }
            // If it was just a tap (no drag), selection was already set on pointerDown
            this._hasDragged = false;
        }
    }

    // ─── Camera sizing + orientation ──────────────────────────────────────────
    // Portrait: rink stands vertically. Landscape: camera rotates 90° so the
    // long COURT_H axis spans horizontally on screen.
    _applyCamera(w, h) {
        if (!w || !h) return;
        const aspect    = w / h;
        const landscape = aspect > 1;
        const minW      = COURT_W + 2;   // 20
        const minH      = COURT_H + 6;   // 36

        if (landscape) {
            this.camera.rotation.z = Math.PI / 2;
            // After 90° rotation, camera left/right maps to screen horizontal (≡ world Y).
            // We need left/right to cover at least COURT_H, top/bottom to cover COURT_W.
            let viewW = minH;
            let viewH = viewW / aspect;
            if (viewH < minW) { viewH = minW; viewW = viewH * aspect; }
            this.camera.left   = -viewW / 2; this.camera.right  =  viewW / 2;
            this.camera.top    =  viewH / 2; this.camera.bottom = -viewH / 2;
        } else {
            this.camera.rotation.z = 0;
            let viewH = minH;
            let viewW = viewH * aspect;
            if (viewW < minW) { viewW = minW; viewH = viewW / aspect; }
            this.camera.left   = -viewW / 2; this.camera.right  =  viewW / 2;
            this.camera.top    =  viewH / 2; this.camera.bottom = -viewH / 2;
        }

        this.camera.updateProjectionMatrix();
    }

    // ─── Resize ───────────────────────────────────────────────────────────────
    _onResize() {
        const w = this.container.clientWidth;
        const h = this.container.clientHeight;
        if (w < 1 || h < 1) return; // skip degenerate sizes (e.g. container not yet laid out)
        this._applyCamera(w, h);
        this.renderer.setSize(w, h, false); // false = don't override CSS 100%/100%
        this.renderer.render(this.scene, this.camera);
    }

    // ─── Destroy ──────────────────────────────────────────────────────────────
    destroy() {
        window.removeEventListener('resize',      this._resizeHandler);
        window.removeEventListener('pointermove', this._pointerMoveHandler);
        window.removeEventListener('pointerup',   this._pointerUpHandler);
        if (this._resizeObserver) this._resizeObserver.disconnect();
        this.renderer.domElement.removeEventListener('pointerdown', this._pointerDownHandler);
        this._clearRubberBand();
        this._stepObjects.forEach(o => this._disposeGroup(o));
        if (this._previewArrow) this._disposeGroup(this._previewArrow);
        this.renderer.dispose();
        if (this.renderer.domElement.parentNode) {
            this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
        }
    }
}
